import { useQuery } from '@tanstack/react-query';
import { issuer_raw } from '@ui/assets/config/issuer-raw';
import { useDataChainId } from '@ui/hooks/datachain';
import { Address, zeroAddress } from 'viem';
import { usePublicClient } from 'wagmi';

import { getGenesisAirdrop } from '../../contract-instances/getGenesisAirdrop';
import { getTransparency } from '../../contract-instances/getTransparency';
import { getValut } from '../../contract-instances/getValut';

export const useTransparencyData = (lvTokenSymbol: 'LVUSD' | 'LVMON') => {
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });
  const issuerConfig = issuer_raw[chainId]?.find(
    (c) => c.lvTokenSymbol === lvTokenSymbol,
  );
  
return useQuery({
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 10000, // auto refresh every 10 seconds
    staleTime: 0,
    gcTime: 0,
    queryKey: ['useTransparencyData', lvTokenSymbol, chainId, client?.uid],
    queryFn: async () => {
      if (!client) throw 'client not found';
      if (!issuerConfig) throw `issuerConfig for ${lvTokenSymbol} not found`;

      const t = getTransparency({
        address: issuerConfig.transparency,
        publicClient: client,
      });

      const [vaults, lvTokenCirculatingSupply] = await Promise.all([
        t.read.getAllVaults(),
        t.read.getLVTokenCirculatingSupply(),
      ]);

      const valutValues = await Promise.all(
        vaults.map(async (v) => {
          const contract = getValut({
            address: v,
            publicClient: client,
          });
          
return contract.read.getReserveLVTokenValue();
        }),
      );

      const totalValutValue = valutValues.reduce((a, b) => a + b, 0n);

      return {
        lvTokenCirculatingSupply,
        totalValutValue,
      };
    },
    enabled: !!client,
  });
};
